import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useAuthSession } from "@/features/auth/hooks/use-auth-session";
import { getAuthErrorMessage } from "@/features/auth/model/auth-errors";
import { AuthorizationFailure } from "@/features/auth/ui/authorization-failure";
import { ApiError } from "@/shared/api/api-error";
import { Callout } from "@/shared/ui/callout";
import { LoadingState } from "@/shared/ui/loading-state";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { loading, isAuthenticated } = useAuth();
  const authSession = useAuthSession();
  const location = useLocation();

  if (loading) {
    return <LoadingState message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    const continuePath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?continue=${encodeURIComponent(continuePath)}`} replace />;
  }

  if (authSession.isPending) {
    return <LoadingState message="Loading session..." />;
  }

  if (authSession.error instanceof ApiError && authSession.error.statusCode === 403) {
    return (
      <AuthorizationFailure
        message={getAuthErrorMessage(authSession.error.code)}
        onReturnToLogin={() => {
          void authSession.refetch();
        }}
      />
    );
  }

  if (authSession.isError) {
    return (
      <Callout severity="error" sx={{ m: 3 }}>
        Session could not be loaded. Please sign in again.
      </Callout>
    );
  }

  return <>{children}</>;
}
